import React from 'react';
import { Link } from 'react-router-dom';

const BusinessApi: React.FC = () => {
  return (
    <div className="bg-white min-h-screen">
      <div className="border-b border-gray-200 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="inline-block mb-4">
            <span className="bg-blue-100 text-blue-700 px-4 py-2 rounded-full text-sm font-semibold">
              Business API
            </span>
          </div>
          <h1 className="text-5xl font-bold text-gray-900 mb-4">
            사업자 데이터 API
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl">
            사업자등록정보, 기업정보, 휴폐업 조회 등 기업 관련 필수 데이터를 제공합니다
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="bg-white rounded-xl border-2 border-gray-200 p-12 mb-12">
          <div className="flex items-center gap-3 mb-12">
            <div className="text-blue-600">
              <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
              </svg>
            </div>
            <h2 className="text-3xl font-bold text-gray-900">
              사업자등록정보 API
            </h2>
          </div>

          <div className="mb-12">
            <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
              엔드포인트
            </h3>
            <div className="bg-gray-900 text-green-400 p-6 rounded-lg font-mono text-sm">
              <span className="inline-block px-3 py-1 bg-yellow-500 text-yellow-900 rounded font-bold mr-3">GET</span>
              <span className="text-gray-100">/api/v1/business/registration</span>
            </div>
          </div>

          <div className="mb-12">
            <h3 className="text-xl font-bold text-gray-900 mb-6 flex items-center gap-2">
              주요 기능
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="border-2 border-gray-200 p-6 rounded-lg hover:border-blue-500 transition-colors">
                <div className="flex items-start gap-3">
                  <div className="p-2 bg-green-100 rounded-lg">
                    <svg className="w-5 h-5 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900 mb-1">사업자 진위 확인</h4>
                    <p className="text-sm text-gray-600">실시간 검증</p>
                  </div>
                </div>
              </div>
              <div className="border-2 border-gray-200 p-6 rounded-lg hover:border-blue-500 transition-colors">
                <div className="flex items-start gap-3">
                  <div className="p-2 bg-green-100 rounded-lg">
                    <svg className="w-5 h-5 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900 mb-1">휴폐업 조회</h4>
                    <p className="text-sm text-gray-600">실시간 상태 확인</p>
                  </div>
                </div>
              </div>
              <div className="border-2 border-gray-200 p-6 rounded-lg hover:border-blue-500 transition-colors">
                <div className="flex items-start gap-3">
                  <div className="p-2 bg-green-100 rounded-lg">
                    <svg className="w-5 h-5 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900 mb-1">신용정보</h4>
                    <p className="text-sm text-gray-600">기업 신용 조회</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="mb-12">
            <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
              응답 예시
            </h3>
            <div className="bg-gray-900 text-gray-100 p-6 rounded-lg font-mono text-sm overflow-x-auto">
              <pre>{`{
  "success": true,
  "data": {
    "business_number": "123-45-67890",
    "business_name": "주식회사 예시기업",
    "ceo_name": "홍길동",
    "status": "active",
    "industry": "정보통신업",
    "registered_date": "2015-03-15",
    "address": "서울특별시 강남구 테헤란로 123"
  }
}`}</pre>
            </div>
          </div>

          <Link
            to="/developer-tools/api-test"
            className="inline-flex items-center gap-2 bg-blue-600 text-white px-8 py-4 rounded-lg hover:bg-blue-700 transition-colors font-semibold text-lg"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            API 테스트하기
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
            </svg>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default BusinessApi;
